import { useCallback, useMemo } from 'react';
import { Select } from 'antd';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Panel from '@/features/report/shared/Panel/Panel';
import type { ContextGroup, ContextLabel } from '@/models/template';
import { useGetWorksheetContextClassesQuery } from '@/state/slices/options';
import panelStyles from '@/features/report/Context/ContextPanel.module.css';
import sectionStyles from '@/features/report/Context/Section/ContextSection.module.css';
import rowStyles from '@/features/report/Context/Row/ContextRow.module.css';
import { EditableText } from '../primitives/EditableText';
import { RowControls } from '../primitives/RowControls';
import { AddButton } from '../primitives/AddButton';
import { humanizeClass, indexToLetter } from '../primitives/format';
import styles from './EditableContextSection.module.css';

const FULL_ROW_THRESHOLD = 5;

interface EditableContextSectionProps {
  groups: ContextGroup[];
  title?: string;
  onChange: (groups: ContextGroup[]) => void;
}

export function EditableContextSection({
  groups,
  title = 'Context',
  onChange,
}: EditableContextSectionProps) {
  const { data: contextClasses = [] } = useGetWorksheetContextClassesQuery();

  const usedClasses = useMemo(() => {
    const used = new Set<string>();
    groups.forEach((g) =>
      g.contextLabels.forEach((cl) => {
        if (cl.contextClass) used.add(cl.contextClass);
      })
    );
    return used;
  }, [groups]);

  const handleGroupChange = useCallback(
    (idx: number, next: ContextGroup) => {
      const copy = [...groups];
      copy[idx] = next;
      onChange(copy);
    },
    [groups, onChange]
  );

  return (
    <Panel title={title}>
      <div className={panelStyles.grid}>
        {groups.map((group, idx) => {
          const isLarge = group.contextLabels.length > FULL_ROW_THRESHOLD;
          return (
            <div
              key={group.uuid}
              className={isLarge ? panelStyles.fullRow : panelStyles.halfRow}
            >
              <EditableContextGroup
                group={group}
                position={idx + 1}
                contextClasses={contextClasses}
                usedClasses={usedClasses}
                onChange={(next) => handleGroupChange(idx, next)}
              />
            </div>
          );
        })}
      </div>
    </Panel>
  );
}

interface EditableContextGroupProps {
  group: ContextGroup;
  position: number;
  contextClasses: string[];
  usedClasses: Set<string>;
  onChange: (group: ContextGroup) => void;
}

function EditableContextGroup({
  group,
  position,
  contextClasses,
  usedClasses,
  onChange,
}: EditableContextGroupProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const sortedRows = useMemo(
    () => [...group.contextLabels].sort((a, b) => a.index - b.index),
    [group.contextLabels]
  );

  const handleRowChange = useCallback(
    (idx: number, next: ContextLabel) => {
      const copy = [...sortedRows];
      copy[idx] = next;
      onChange({ ...group, contextLabels: copy });
    },
    [group, onChange, sortedRows]
  );

  const handleRowDelete = useCallback(
    (idx: number) => {
      onChange({
        ...group,
        contextLabels: sortedRows.filter((_, i) => i !== idx),
      });
    },
    [group, onChange, sortedRows]
  );

  const handleAddRow = useCallback(() => {
    const maxIndex = sortedRows.reduce((m, cl) => Math.max(m, cl.index), -1);
    const now = new Date().toISOString();
    const newLabel: ContextLabel = {
      uuid: crypto.randomUUID(),
      label: '',
      contextClass: '',
      index: maxIndex + 1,
      createdAt: now,
      updatedAt: now,
    };
    onChange({ ...group, contextLabels: [...sortedRows, newLabel] });
  }, [group, onChange, sortedRows]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIdx = sortedRows.findIndex((r) => r.uuid === active.id);
      const newIdx = sortedRows.findIndex((r) => r.uuid === over.id);
      if (oldIdx === -1 || newIdx === -1) return;
      const reordered = arrayMove(sortedRows, oldIdx, newIdx).map((r, i) => ({
        ...r,
        index: i,
      }));
      onChange({ ...group, contextLabels: reordered });
    },
    [group, onChange, sortedRows]
  );

  return (
    <div className={sectionStyles.section}>
      <div className={sectionStyles.header}>
        <span className={sectionStyles.accentBar} />
        <span className={sectionStyles.title}>
          {position}.{' '}
          <EditableText
            value={group.label}
            onChange={(label) => onChange({ ...group, label })}
            placeholder="Group name"
          />
        </span>
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={sortedRows.map((r) => r.uuid)}
          strategy={rectSortingStrategy}
        >
          <div className={sectionStyles.content}>
            {sortedRows.map((cl, idx) => (
              <EditableContextRow
                key={cl.uuid}
                label={cl}
                position={indexToLetter(idx)}
                contextClasses={contextClasses}
                usedClasses={usedClasses}
                onChange={(next) => handleRowChange(idx, next)}
                onDelete={() => handleRowDelete(idx)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <div className={styles.addRowWrap}>
        <AddButton onClick={handleAddRow} label="Add field" />
      </div>
    </div>
  );
}

interface EditableContextRowProps {
  label: ContextLabel;
  position: string;
  contextClasses: string[];
  usedClasses: Set<string>;
  onChange: (label: ContextLabel) => void;
  onDelete: () => void;
}

function EditableContextRow({
  label,
  position,
  contextClasses,
  usedClasses,
  onChange,
  onDelete,
}: EditableContextRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: label.uuid });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Filter out context classes already used by other rows; always include
  // the current row's selection so it remains visible in its own picker.
  // Synthesize a fallback option if the stored class isn't in the options
  // list (loading or stale) so the Select can render the current value.
  const options = useMemo(() => {
    const list = contextClasses
      .filter((c) => !usedClasses.has(c) || c === label.contextClass)
      .map((c) => ({
        value: c,
        label: humanizeClass(c),
      }));

    const current = label.contextClass;
    if (current && !list.some((o) => o.value === current)) {
      list.push({ value: current, label: humanizeClass(current) });
    }
    return list;
  }, [contextClasses, usedClasses, label.contextClass]);

  const handleLabelChange = (value: string) => {
    onChange({ ...label, label: value });
  };

  const handleClassSelect = (value: string | undefined) => {
    onChange({ ...label, contextClass: value ?? '' });
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${rowStyles.row} ${styles.draggableRow}`}
      data-editable-row
      {...attributes}
      {...listeners}
    >
      <span className={`${rowStyles.label} ${styles.labelCell}`}>
        <span className={styles.positionBadge}>{position}.</span>
        <EditableText
          value={label.label}
          onChange={handleLabelChange}
          placeholder="Label"
        />
      </span>
      <div className={rowStyles.valueContainer}>
        <Select
          variant="borderless"
          size="small"
          placeholder="Pick field"
          options={options}
          value={label.contextClass || undefined}
          onChange={handleClassSelect}
          showSearch
          allowClear
          optionFilterProp="label"
          popupMatchSelectWidth={260}
          className={styles.classSelect}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
      <RowControls onDelete={onDelete} className={styles.rowControls} />
    </div>
  );
}
